import React, { useState, useEffect, useRef } from "react";

function get(obj, path, fallback) {
  var result = obj;
  var parts = path.split(".");
  for (var i = 0; i < parts.length; i++) {
    if (result === null || result === undefined) return fallback;
    result = result[parts[i]];
  }
  return result === undefined || result === null ? fallback : result;
}

function cleanHtmlTags(val) {
  if (typeof val === "string") {
    return val.replace(/<\/?p>/gi, " ").trim();
  }
  if (Array.isArray(val)) {
    return val.map(item => cleanHtmlTags(item));
  }
  if (val !== null && typeof val === "object") {
    const cleaned = {};
    for (let k in val) cleaned[k] = cleanHtmlTags(val[k]);
    return cleaned;
  }
  return val;
}

function getInitialValue(val) {
  if (val === null || val === undefined) return "";
  return cleanHtmlTags(val);
}

function setDeep(obj, path, newValue) {
  const root = Array.isArray(obj) ? [...obj] : { ...obj };
  let current = root;
  for (let i = 0; i < path.length - 1; i++) {
    const key = path[i];
    current[key] = Array.isArray(current[key]) ? [...current[key]] : { ...current[key] };
    current = current[key];
  }
  current[path[path.length - 1]] = newValue;
  return root;
}

const AutoResizeTextarea = ({ value, onChange, readOnly, style, placeholder }) => {
  const textareaRef = useRef(null);

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      const extraRowHeight = 18; 
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight + extraRowHeight}px`;
    }
  }, [value]);

  return (
    <textarea
      ref={textareaRef}
      style={{
        ...style,
        resize: "none",
        overflow: "hidden",
        boxSizing: "border-box",
        fontFamily: "inherit",
        lineHeight: "1.4"
      }}
      value={value}
      onChange={onChange}
      readOnly={readOnly}
      placeholder={placeholder}
      rows={1} 
    />
  );
};

function AmazonProductListingEditableField({ field = {}, onFieldChange, changedValue, isUpdated }) {
  var rawValue = isUpdated ? changedValue : get(field, "listing.value", null);
  var initial = getInitialValue(rawValue);

  var [value, setValue] = useState(initial);

  var options = get(field, "schema.allowed_values", []) || [];
  
  var isDropdown = get(field, "ui.input_type", "") === "dropdown" || options.length > 0;
  var isEditable = get(field, "schema.editable", true);

  var examples = get(field, "schema.examples", []) || [];
  var placeholder = examples.length > 0 ? `e.g. ${examples[0]}` : "";

  var inputStyle = {
    width: "100%", 
    fontSize: 12, 
    padding: "4px 8px", 
    borderRadius: 6,
    border: "1px solid #ccc", 
    background: "var(--color-background-primary, #ffffff)", 
    color: "#333", 
    cursor: "text" 
  };

  var plainTextStyle = {
    width: "100%",
    fontSize: 12,
    padding: "4px 0px", 
    color: "#555",
    wordBreak: "break-word"
  };

  const getDisplayText = (val) => {
    if (val === null || val === undefined) return "—";
    const strVal = String(val).trim();
    return strVal === "" ? "—" : strVal;
  };

  const handleStandardChange = (newVal) => {
    if (!isEditable) return;
    setValue(newVal);
    if (onFieldChange) onFieldChange({ field: field.field, value: newVal });
  };

  const handleDeepChange = (path, newVal) => {
    if (!isEditable) return;
    const updatedObject = setDeep(value, path, newVal);
    setValue(updatedObject);
    if (onFieldChange) onFieldChange({ field: field.field, value: updatedObject });
  };

  useEffect(() => {
    setValue(isUpdated ? changedValue : getInitialValue(get(field, "listing.value", null)));
  }, [field, changedValue, isUpdated]);

  if (isDropdown) {
    let selectedValue = "";
    if (value !== null && value !== undefined) {
      if (Array.isArray(value) && value.length > 0) {
        selectedValue = String(value[0]);
      } else if (typeof value !== "object") {
        selectedValue = String(value);
      }
    }

    if (!isEditable) {
      return <div style={plainTextStyle}>{getDisplayText(selectedValue)}</div>;
    }

    return (
      <select
        style={inputStyle}
        value={selectedValue}
        onChange={(e) => {
          if (!isEditable) return;
          const originalIsArray = Array.isArray(get(field, "listing.value", null));
          const newVal = originalIsArray ? [e.target.value] : e.target.value;
          
          setValue(newVal);
          if (onFieldChange) onFieldChange({ field: field.field, value: newVal });
        }}
      >
        <option value="">
          {placeholder || "Select"}
        </option>

        {options.map((opt) => (
          <option key={String(opt)} value={String(opt)}>
            {String(opt)}
          </option>
        ))}
      </select>
    );
  }

  const renderDeep = (data, path = [], isObjProp = false) => {
    if (data === null || data === undefined) {
      if (!isEditable) return <div style={{ ...plainTextStyle, width: isObjProp ? "60%" : "100%" }}>—</div>;
      return null;
    }

    if (typeof data !== "object") {
      if (!isEditable) {
        return (
          <div style={{ ...plainTextStyle, width: isObjProp ? "60%" : "100%" }}>
            {getDisplayText(data)}
          </div>
        );
      }

      return (
        <AutoResizeTextarea
          style={{ ...inputStyle, width: isObjProp ? "60%" : "100%", marginBottom: 0 }}
          value={String(data)}
          onChange={(e) => handleDeepChange(path, e.target.value)}
          placeholder={placeholder}
        />
      );
    }

    if (Array.isArray(data)) {
      return (
        <div style={{ display: "flex", flexDirection: "column", width: "100%" }}>
          {data.map((item, index) => {
            const isPrimitive = typeof item !== "object" || item === null;
            return (
              <div 
                key={index} 
                style={{ 
                  marginBottom: isPrimitive ? "4px" : (index < data.length - 1 ? "8px" : "0")
                }}
              >
                {renderDeep(item, [...path, index])}
              </div>
            );
          })}
        </div>
      );
    }

    if (typeof data === "object") {
      return (
        <div style={{ display: "flex", flexDirection: "column", width: "100%" }}>
          {Object.entries(data).map(([k, v]) => {
            const currentPath = [...path, k];
            
            if (typeof v !== "object" || v === null) {
              return (
                <div key={k} style={{ display: "flex", alignItems: "center", marginBottom: "4px" }}>
                  <span style={{ fontSize: "11px", fontWeight: "500", width: "35%", paddingRight: "5px", color: "#555", wordBreak: "break-word" }}>
                    {k}:
                  </span>
                  {renderDeep(v, currentPath, true)}
                </div>
              );
            }
            
            return (
              <div key={k} style={{ marginBottom: "6px", backgroundColor: "#fdfdfd", padding: "6px", borderRadius: "4px", border: "1px solid #ebebeb" }}>
                <div style={{ fontSize: "11px", fontWeight: "bold", color: "#333", marginBottom: "4px", textTransform: "capitalize" }}>
                  {k.replace(/_/g, " ")}
                </div>
                <div style={{ paddingLeft: "4px" }}>
                  {renderDeep(v, currentPath)}
                </div>
              </div>
            );
          })}
        </div>
      );
    }

    return null;
  };

  if (value !== null && typeof value === "object") {
    return renderDeep(value);
  }

  if (!isEditable) {
    return (
      <div style={plainTextStyle}>
        {getDisplayText(value)}
      </div>
    );
  }

  return (
    <AutoResizeTextarea
      style={inputStyle}
      value={value !== null && value !== undefined ? String(value) : ""}
      onChange={(e) => handleStandardChange(e.target.value)}
      placeholder={placeholder}
    />
  );
}

export default AmazonProductListingEditableField;